import {
  paymentChannelForMethod,
  resolvePaymentMethodKey,
  methodSortIndex,
} from './paymentMethods';
import { CLINIC_TIMEZONE, zonedDateTime, zonedYmd } from './timezone';
import { isOutstandingInvoice as isSharedOutstanding } from '@/lib/outstandingInvoice';
import type { DateRange } from './types';
import type {
  CashierCashSection,
  CashierClinioPaySection,
  CashierFilterState,
  CashierMethodBreakdown,
  CashierOutstandingInvoice,
  CashierPaymentRow,
  CashierPeriodGrain,
  CashierPeriodRow,
  CashierReportResult,
  CashierSummary,
} from './cashierTypes';
import { emptyCashierSummary } from './cashierTypes';

export const CASHIER_DETAIL_LIMIT = 2000;
export const CASHIER_EXPORT_LIMIT = 10_000;

/** Same statuses as public.outstanding_invoices. Credit notes are excluded by kind. */
export const OUTSTANDING_INVOICE_STATUSES = ['issued', 'partial', 'overdue'] as const;

export function outstandingBalanceCents(totalCents: number, paidCents: number): number {
  return Math.max(0, (Number(totalCents) || 0) - (Number(paidCents) || 0));
}

export function isOutstandingInvoice(input: {
  status: string;
  total_cents: number;
  paid_cents: number;
  kind?: string | null;
}): boolean {
  return isSharedOutstanding({
    kind: input.kind,
    status: input.status,
    totalCents: input.total_cents,
    paidCents: input.paid_cents,
  });
}

export function matchesOutstandingInvoiceFilters(
  row: CashierOutstandingInvoice,
  filters: Pick<CashierFilterState, 'practitionerIds' | 'search'>,
): boolean {
  if (filters.practitionerIds.length > 0) {
    if (!row.related_practitioner_ids.some((id) => filters.practitionerIds.includes(id))) {
      return false;
    }
  }
  const q = filters.search.trim().toLowerCase();
  if (q) {
    const haystack = [row.patient_name, row.invoice_number].filter(Boolean).join(' ').toLowerCase();
    if (!haystack.includes(q)) return false;
  }
  return true;
}

export function toCashierOutstandingInvoice(input: {
  id: string;
  invoice_number?: string | null;
  patient_id?: string | null;
  patient_name?: string | null;
  issued_at?: string | null;
  due_at?: string | null;
  total_cents: number;
  paid_cents?: number;
  status: string;
  clinic_id: string;
  clinic_name?: string | null;
  related_practitioner_ids?: string[];
}): CashierOutstandingInvoice | null {
  const paid = input.paid_cents ?? 0;
  if (!isOutstandingInvoice({ status: input.status, total_cents: input.total_cents, paid_cents: paid })) {
    return null;
  }
  return {
    id: input.id,
    invoice_number: input.invoice_number ?? null,
    patient_id: input.patient_id ?? null,
    patient_name: input.patient_name ?? null,
    issued_at: input.issued_at ?? null,
    due_at: input.due_at ?? null,
    total_cents: input.total_cents,
    paid_cents: paid,
    outstanding_cents: outstandingBalanceCents(input.total_cents, paid),
    status: input.status,
    clinic_id: input.clinic_id,
    clinic_name: input.clinic_name ?? null,
    related_practitioner_ids: input.related_practitioner_ids ?? [],
  };
}

export function chooseCashierGrain(period: DateRange): CashierPeriodGrain {
  const ms = period.end.getTime() - period.start.getTime();
  const hours = ms / 3_600_000;
  if (hours <= 36) return 'hour';
  const days = hours / 24;
  if (days <= 45) return 'day';
  if (days <= 180) return 'week';
  return 'month';
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

export function cashierBucketKey(
  iso: string,
  grain: CashierPeriodGrain,
  timeZone = CLINIC_TIMEZONE,
): string {
  const date = new Date(iso);
  const z = zonedDateTime(date, timeZone);
  if (grain === 'hour') {
    return `${z.year}-${pad(z.month)}-${pad(z.day)}T${pad(z.hour)}:00`;
  }
  if (grain === 'day') {
    return `${z.year}-${pad(z.month)}-${pad(z.day)}`;
  }
  if (grain === 'week') {
    const weekday = new Date(Date.UTC(z.year, z.month - 1, z.day)).getUTCDay();
    const mondayOffset = weekday === 0 ? 6 : weekday - 1;
    const monday = new Date(Date.UTC(z.year, z.month - 1, z.day - mondayOffset));
    return `${monday.getUTCFullYear()}-${pad(monday.getUTCMonth() + 1)}-${pad(monday.getUTCDate())}`;
  }
  return `${z.year}-${pad(z.month)}`;
}

export function matchesCashierFilters(
  row: CashierPaymentRow,
  filters: Pick<
    CashierFilterState,
    'paymentMethods' | 'channels' | 'practitionerIds' | 'statuses' | 'search'
  >,
): boolean {
  if (filters.paymentMethods.length > 0 && !filters.paymentMethods.includes(row.payment_method_key)) {
    return false;
  }
  if (filters.channels.length > 0 && !filters.channels.includes(row.channel)) {
    return false;
  }
  if (filters.practitionerIds.length > 0) {
    const related =
      row.related_practitioner_ids.length > 0
        ? row.related_practitioner_ids
        : row.practitioner_id
          ? [row.practitioner_id]
          : [];
    if (!related.some((id) => filters.practitionerIds.includes(id))) return false;
  }
  if (filters.statuses.length > 0 && !filters.statuses.includes(row.status)) {
    return false;
  }
  const q = filters.search.trim().toLowerCase();
  if (q) {
    const haystack = [
      row.patient_name,
      row.invoice_number,
      row.payment_reference,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    if (!haystack.includes(q)) return false;
  }
  return true;
}

function emptyMethod(methodKey: string): CashierMethodBreakdown {
  return {
    method_key: methodKey,
    received_cents: 0,
    refund_cents: 0,
    net_cents: 0,
    transaction_count: 0,
    refund_count: 0,
  };
}

function sortMethods(items: CashierMethodBreakdown[]): CashierMethodBreakdown[] {
  return [...items].sort((a, b) => {
    const order = methodSortIndex(a.method_key) - methodSortIndex(b.method_key);
    if (order !== 0) return order;
    return b.received_cents - a.received_cents || a.method_key.localeCompare(b.method_key);
  });
}

export function aggregateCashierRows(
  rows: CashierPaymentRow[],
  period: DateRange,
  options: {
    timezone?: string;
    clinicName?: string | null;
    availableMethods?: string[];
    truncated?: boolean;
    totalCount?: number;
    grain?: CashierPeriodGrain;
    outstandingInvoices?: CashierOutstandingInvoice[];
  } = {},
): CashierReportResult {
  const timezone = options.timezone ?? CLINIC_TIMEZONE;
  const grain = options.grain ?? chooseCashierGrain(period);
  const methods = new Map<string, CashierMethodBreakdown>();
  const periodMap = new Map<string, CashierPeriodRow>();
  const summary: CashierSummary = emptyCashierSummary();

  for (const row of rows) {
    const method = methods.get(row.payment_method_key) ?? emptyMethod(row.payment_method_key);
    if (row.kind === 'payment') {
      summary.total_received_cents += row.amount_cents;
      summary.transaction_count += 1;
      method.received_cents += row.amount_cents;
      method.transaction_count += 1;
    } else {
      const refund = Math.abs(row.amount_cents);
      summary.refunds_cents += refund;
      summary.refund_count += 1;
      method.refund_cents += refund;
      method.refund_count += 1;
    }
    method.net_cents = method.received_cents - method.refund_cents;
    methods.set(row.payment_method_key, method);

    const bucket = cashierBucketKey(row.payment_date, grain, timezone);
    const periodRow = periodMap.get(bucket) ?? {
      bucket,
      label: bucket,
      received_cents: 0,
      refund_cents: 0,
      net_cents: 0,
      transaction_count: 0,
      refund_count: 0,
    };
    if (row.kind === 'payment') {
      periodRow.received_cents += row.amount_cents;
      periodRow.transaction_count += 1;
    } else {
      periodRow.refund_cents += Math.abs(row.amount_cents);
      periodRow.refund_count += 1;
    }
    periodRow.net_cents = periodRow.received_cents - periodRow.refund_cents;
    periodMap.set(bucket, periodRow);
  }

  summary.net_received_cents = summary.total_received_cents - summary.refunds_cents;
  const outstandingInvoices = [...(options.outstandingInvoices ?? [])].sort((a, b) =>
    `${a.issued_at ?? ''}${a.invoice_number ?? ''}${a.id}`.localeCompare(
      `${b.issued_at ?? ''}${b.invoice_number ?? ''}${b.id}`,
    ),
  );
  summary.outstanding_cents = outstandingInvoices.reduce((sum, row) => sum + row.outstanding_cents, 0);
  summary.outstanding_count = outstandingInvoices.length;

  const methodList = sortMethods([...methods.values()]);
  const cashMethod = methods.get('cash') ?? emptyMethod('cash');
  const cash: CashierCashSection = {
    received_cents: cashMethod.received_cents,
    refund_cents: cashMethod.refund_cents,
    net_cents: cashMethod.net_cents,
    transaction_count: cashMethod.transaction_count,
    refund_count: cashMethod.refund_count,
  };

  const payMethod = methods.get('clinio_pay') ?? emptyMethod('clinio_pay');
  const clinioPay: CashierClinioPaySection = {
    received_cents: payMethod.received_cents,
    refund_cents: payMethod.refund_cents,
    net_cents: payMethod.net_cents,
    transaction_count: payMethod.transaction_count,
    refund_count: payMethod.refund_count,
    fees_cents: null,
    payout_received_cents: null,
    expected_payout_cents: null,
  };

  const periodRows = [...periodMap.values()].sort((a, b) => a.bucket.localeCompare(b.bucket));
  const availableMethods = options.availableMethods ?? methodList.map((item) => item.method_key);

  return {
    titleKey: 'reports.types.cashier.label',
    period,
    grain,
    timezone,
    currency: rows[0]?.currency ?? 'DKK',
    clinic_name: options.clinicName ?? rows[0]?.clinic_name ?? null,
    summary,
    methods: methodList,
    refunds: methodList.filter((item) => item.refund_cents > 0),
    cash,
    clinioPay,
    periodRows,
    transactions: [...rows].sort((a, b) => b.payment_date.localeCompare(a.payment_date)),
    transactionsTruncated: Boolean(options.truncated),
    transactionTotalCount: options.totalCount ?? rows.length,
    outstandingInvoices,
    availableMethods,
    empty:
      rows.length === 0 &&
      summary.transaction_count === 0 &&
      summary.refund_count === 0 &&
      outstandingInvoices.length === 0,
  };
}

export function toCashierPaymentRow(input: {
  id: string;
  payment_date: string;
  amount_cents: number;
  currency?: string;
  payment_method?: string | null;
  fallback_payment_method?: string | null;
  payment_reference?: string | null;
  notes?: string | null;
  invoice_id?: string | null;
  invoice_number?: string | null;
  patient_id?: string | null;
  patient_name?: string | null;
  practitioner_id?: string | null;
  practitioner_name?: string | null;
  related_practitioner_ids?: string[];
  clinic_id: string;
  clinic_name?: string | null;
}): CashierPaymentRow {
  const isRefund = input.amount_cents < 0;
  const methodKey = resolvePaymentMethodKey(
    input.payment_method,
    input.fallback_payment_method,
  );
  return {
    id: input.id,
    payment_date: input.payment_date,
    amount_cents: isRefund ? -Math.abs(input.amount_cents) : Math.abs(input.amount_cents),
    currency: input.currency ?? 'DKK',
    payment_method_raw: input.payment_method ?? null,
    payment_method_key: methodKey,
    channel: paymentChannelForMethod(methodKey),
    payment_reference: input.payment_reference ?? null,
    notes: input.notes ?? null,
    invoice_id: input.invoice_id ?? null,
    invoice_number: input.invoice_number ?? null,
    patient_id: input.patient_id ?? null,
    patient_name: input.patient_name ?? null,
    practitioner_id: input.practitioner_id ?? null,
    practitioner_name: input.practitioner_name ?? null,
    related_practitioner_ids: input.related_practitioner_ids ?? [],
    clinic_id: input.clinic_id,
    clinic_name: input.clinic_name ?? null,
    kind: isRefund ? 'refund' : 'payment',
    status: isRefund ? 'refunded' : 'received',
  };
}

export function formatCashierBucketLabel(
  bucket: string,
  grain: CashierPeriodGrain,
  _locale = 'en-GB',
): string {
  if (grain === 'hour') {
    const [date, time] = bucket.split('T');
    const [year, month, day] = (date ?? '').split('-');
    if (!year || !month || !day || !time) return bucket;
    return `${day}-${month}-${year} kl. ${time}`;
  }
  if (grain === 'month') {
    const [year, month] = bucket.split('-');
    if (!year || !month) return bucket;
    return `${month}-${year}`;
  }
  const [year, month, day] = bucket.split('-');
  if (!year || !month || !day) return bucket;
  return `${day}-${month}-${year}`;
}

export function countCashierFilters(filters: CashierFilterState): number {
  let count = 0;
  if (filters.period !== 'this_month') count += 1;
  if (filters.paymentMethods.length > 0) count += 1;
  if (filters.channels.length > 0) count += 1;
  if (filters.practitionerIds.length > 0) count += 1;
  if (filters.locationIds.length > 0) count += 1;
  if (filters.statuses.length > 0) count += 1;
  if (filters.search.trim()) count += 1;
  return count;
}

export { zonedYmd };
